import { LiveDataRow } from "../models/LiveDataRow";
import { newestOf } from "./newestReading";

/** One line of the vehicle checklist: was this value reported, and what was it. */
export interface CheckItem {
  label: string;
  value: string;
  found: boolean;
  /** The value came from before the period rather than from it (yellow). */
  fromFallback: boolean;
  /** The value lies further before the period than the user's limit (red). */
  stale?: boolean;
  /** When a value from before the period was reported; it says how far behind the line is. */
  lastReportedAt?: Date;
  /** The value was entered by hand rather than sent by the vehicle; `lastReportedAt` says when. */
  manual?: boolean;
  /**
   * How far this odometer is off the other one the vehicle sent at the same moment, formatted.
   * Sent together they should read the same; a gap means one of them is wrong (red).
   */
  mismatch?: string;
  /**
   * The diagnostic the value was read off, shown under the label.
   *
   * An installer comparing the checklist against the database needs the handle Geotab uses, not
   * only the wording — names arrive in the database's language and several read alike. Absent on
   * the lines that answer from something other than a reading, the firmware for one.
   */
  diagnosticId?: string;
  /** When the value was reported, shown on hover. Absent where no reading stands behind it. */
  dateTime?: Date;
  /** What the value means, shown when pointing at it; a harness code says which harness it is. */
  valueHint?: string;
}

export type RowPredicate = (row: LiveDataRow) => boolean;

export interface SpecificRowMatch {
  predicate: RowPredicate;
  specificity: (row: LiveDataRow) => number;
}

/** How to recognise the reading a check is asking about. */
export type RowMatch = RowPredicate | SpecificRowMatch;

export type ValueFormatter = (row: LiveDataRow | undefined) => string;

/** The newest reading among those the matcher ranks highest. */
function latestMatching(
  rows: LiveDataRow[],
  matcher: (row: LiveDataRow) => number,
): LiveDataRow | undefined {
  let bestSpecificity = 0;
  let candidates: LiveDataRow[] = [];

  for (const row of rows) {
    const specificity = matcher(row);
    if (specificity <= 0) {
      continue;
    }
    if (specificity > bestSpecificity) {
      bestSpecificity = specificity;
      candidates = [row];
    } else if (specificity === bestSpecificity) {
      candidates.push(row);
    }
  }

  return newestOf(candidates, row => row.dateTime);
}

export interface CreateCheckItemInput {
  label: string;
  /** Readings from the live window. */
  rows: LiveDataRow[];
  /** Readings from the wider history, used only when the live window has nothing. */
  fallbackRows: LiveDataRow[];
  /** The last reading of each diagnostic however old, used only when the history has nothing. */
  lastKnownRows?: LiveDataRow[];
  match: RowMatch;
  format: ValueFormatter;
  /**
   * A value from before the period reported earlier than this is stale. Left out, only the last
   * reading ever is.
   */
  staleBefore?: Date;
  /** Recognises the readings that were entered by hand, so the line can say so. */
  manual?: RowPredicate;
  /** When true, found is derived from the raw row value being 1 rather than the formatted string being non-empty. */
  boolean?: boolean;
  /** Explains the reading the line answers with, for a value that is a code rather than a figure. */
  hint?: (row: LiveDataRow) => string | undefined;
}

/**
 * Answers one check from the readings.
 *
 * A vehicle parked overnight reports nothing live, so the check falls back to the history — but
 * says which it used, because "reported an hour ago" and "reported yesterday" mean different
 * things to someone verifying an installation.
 */
export function createCheckItem(input: CreateCheckItemInput): CheckItem {
  const {
    label,
    rows,
    fallbackRows,
    lastKnownRows = [],
    match,
    format,
    staleBefore,
    manual,
    boolean: isBoolean,
    hint,
  } = input;
  const matcher = toMatcher(match);

  const live = latestMatching(rows, matcher);
  // An unusable live value (formats to "", or a non-finite raw value for a boolean check) must
  // not block the fallback to history — otherwise a live reading that happens to be NaN reports
  // "not found" even though a perfectly good historical value is sitting right there.
  const isUsable = (row: LiveDataRow) =>
    isBoolean ? Number.isFinite(row.value) : format(row) !== "";
  const liveUsable = live !== undefined && isUsable(live);
  const fallback = liveUsable ? undefined : latestMatching(fallbackRows, matcher);
  const fallbackUsable = fallback !== undefined && isUsable(fallback);
  const lastKnown =
    liveUsable || fallbackUsable ? undefined : latestMatching(lastKnownRows, matcher);
  const matchedRow = liveUsable ? live : fallbackUsable ? fallback : (lastKnown ?? fallback);
  const value = format(matchedRow);

  let found: boolean;
  if (isBoolean) {
    found = matchedRow !== undefined && matchedRow.value === 1;
  } else {
    found = value !== "";
  }

  const fromFallback = found && matchedRow !== live;
  const reportedAt = matchedRow?.dateTime;
  const stale =
    fromFallback &&
    (staleBefore && reportedAt
      ? reportedAt.getTime() < staleBefore.getTime()
      : lastKnown !== undefined && matchedRow === lastKnown);
  const isManual = found && matchedRow !== undefined && manual?.(matchedRow) === true;
  const dated = stale || isManual || (fromFallback && staleBefore !== undefined);
  const valueHint = found && matchedRow && hint ? hint(matchedRow) : undefined;

  return {
    label,
    value,
    found,
    fromFallback,
    ...(stale && { stale }),
    ...(isManual && { manual: true }),
    ...(dated && { lastReportedAt: reportedAt }),
    // Empty ids are dropped rather than passed on, so the line shows no id at all instead of an
    // empty space under its name.
    diagnosticId: matchedRow?.diagnosticId || undefined,
    dateTime: found ? matchedRow?.dateTime : undefined,
    ...(valueHint && { valueHint }),
  };
}

function toMatcher(match: RowMatch): (row: LiveDataRow) => number {
  if (typeof match === "function") {
    return row => (match(row) ? 1 : 0);
  }

  return row => (match.predicate(row) ? match.specificity(row) : 0);
}
